/**
 * Core utility functions for working with localStorage
 */

// Storage keys
export const STORAGE_KEYS = {
  WORKOUT_SESSIONS: 'workoutSessions',
  DATA_VERSION: 'dataVersion',
};

// Current data version
export const CURRENT_DATA_VERSION = 1;

/**
 * Safe wrapper for localStorage setItem that handles errors
 * @param key - The key to store
 * @param value - The value to store
 * @returns True if successful, false if error occurred
 */
export const safeSetItem = (key: string, value: string): boolean => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.error(`Error storing data for key '${key}':`, error);
    return false;
  }
};

/**
 * Safe wrapper for localStorage getItem that handles errors
 * @param key - The key to retrieve
 * @returns The value or null if not found/error
 */
export const safeGetItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.error(`Error retrieving data for key '${key}':`, error);
    return null;
  }
};

/**
 * Safe wrapper for localStorage removeItem that handles errors
 * @param key - The key to remove
 * @returns True if successful, false if error occurred
 */
export const safeRemoveItem = (key: string): boolean => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing data for key '${key}':`, error);
    return false;
  }
};

/**
 * Check if data version is current, and update if needed
 * @returns True if data is current version
 */
export const checkDataVersion = (): boolean => {
  const storedVersion = parseInt(safeGetItem(STORAGE_KEYS.DATA_VERSION) || '0', 10);

  if (storedVersion < CURRENT_DATA_VERSION) {
    // In future versions, we'd add migration code here

    // Update the version
    safeSetItem(STORAGE_KEYS.DATA_VERSION, CURRENT_DATA_VERSION.toString());
    return false;
  }

  return true;
};
